import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faShieldAlt,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faInfoCircle,
  faChevronDown,
  faEraser,
  faTimes,
  faChartBar,
  faExchangeAlt,
} from "@fortawesome/free-solid-svg-icons";
import "../css/URLSafetyAnalysis.css";
import "../../css/Common.css";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import ThreatBannerShareButton from "../common/ThreatBannerShareButton";
import EmptyStatePanel from "../common/EmptyStatePanel";
import DisclosuresModal from "../common/DisclosuresModal";
import HelpGuideButton from "../../HelpGuideButton";
import { useTranslation } from "../../../../i18n/LanguageContext";

const URLSafetyAnalysisDesktop = ({
  url,
  setUrl,
  context,
  setContext,
  analysisResult,
  error,
  reportId,
  handleSubmit,
  handleClear,
  setHeroContent,
}) => {
  const { t } = useTranslation();

  const getThreatBannerClass = () => {
    if (analysisResult?.isScam === true) return "threat-detected";
    if (analysisResult?.isScam === false) return "safe";
    return "security-failed";
  };

  const getThreatIcon = () => {
    if (analysisResult?.isScam === true) return faTimesCircle;
    if (analysisResult?.isScam === false) return faCheckCircle;
    return faExclamationTriangle;
  };

  const renderInputForm = () => (
    <div className="url-input-section">
      <form onSubmit={handleSubmit} className="url-form">
        <div className="form-group">
          <label htmlFor="url-input">
            <FontAwesomeIcon icon={faLink} className="label-icon" />
            {t("urlsa.form.urlLabel")}
          </label>
          <div className="input-wrapper">
            <div className="input-with-clear">
              <input
                type="text"
                id="url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t("urlsa.form.urlPlaceholder")}
                className="url-input"
                autoComplete="off"
                required
              />
              {url && (
                <button
                  type="button"
                  className="clear-input-button"
                  onClick={() => setUrl("")}
                  aria-label={t("urlsa.form.clearAriaLabel")}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="context-input">
            <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
            {t("urlsa.form.contextLabel")}
          </label>
          <textarea
            id="context-input"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t("urlsa.form.contextPlaceholder")}
            className="additional-context-textarea"
            rows={3}
          />
        </div>
        <div className="button-row">
          <button type="submit" className="analyze-button">
            <FontAwesomeIcon icon={faShieldAlt} className="button-icon" />
            {t("urlsa.form.analyzeBtn")}
          </button>
          {(url || context || analysisResult || error) && (
            <button
              type="button"
              className="clear-all-button"
              onClick={handleClear}
            >
              <FontAwesomeIcon icon={faEraser} className="button-icon" />
              {t("urlsa.form.clearBtn")}
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
        </div>
      )}
    </div>
  );

  const renderResultsContent = () => {
    if (!analysisResult) return null;

    return (
      <div className="analysis-results">
        {analysisResult.wasRedirect && (
          <details className="redirect-notice">
            <summary className="redirect-notice-summary">
              <div className="redirect-notice-icon">
                <FontAwesomeIcon icon={faExchangeAlt} />
              </div>
              <span className="redirect-notice-title">
                <strong>{t("urlsa.redirect.detected")}</strong>{" "}
                {t("urlsa.redirect.summary").replace(
                  "{platform}",
                  analysisResult.wrapperPlatform,
                )}
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="redirect-chevron"
              />
            </summary>
            <div className="redirect-notice-content">
              {t("urlsa.redirect.body").replace(
                "{platform}",
                analysisResult.wrapperPlatform,
              )}
              <div className="redirect-urls">
                <span className="redirect-label">
                  {t("urlsa.redirect.youSubmitted")}
                </span>{" "}
                <span className="redirect-url original">
                  {analysisResult.originalUrl}
                </span>
                <span className="redirect-arrow">→</span>
                <span className="redirect-label">
                  {t("urlsa.redirect.actualDest")}
                </span>{" "}
                <span className="redirect-url resolved">
                  {analysisResult.url}
                </span>
              </div>
            </div>
          </details>
        )}

        <div className="result-content">
          <div
            className={`threat-banner urlsa-threat-banner ${getThreatBannerClass()}`}
          >
            <div className="threat-icon">
              <FontAwesomeIcon icon={getThreatIcon()} />
            </div>
            <h2 className="threat-title">
              {analysisResult?.isScam
                ? t("urlsa.threat.scamDetected")
                : t("urlsa.threat.noThreats")}
            </h2>
            <ThreatBannerShareButton
              scamDetected={analysisResult.isScam === true}
              threatType={t("urlsa.share.threatType")}
              threatTitle={t("urlsa.share.threatTitle")}
              threatDetail={`URL: ${analysisResult.url || url}`}
              reportId={reportId}
            />
          </div>

          {analysisResult.suggestions && (
            <div className="recommendations">
              <h3 className="section-title">{t("urlsa.results.whatToDo")}</h3>
              <SuggestionsBulletList text={analysisResult.suggestions} />
            </div>
          )}

          <details className="detailed-analysis-collapsible" open>
            <summary className="section-title collapsible-summary">
              {t("urlsa.results.detailedAnalysis")}
              <FontAwesomeIcon
                icon={faChevronDown}
                className="collapsible-chevron"
                style={{ marginLeft: "0.75rem" }}
              />
            </summary>
            <div
              className="summary-text"
              dangerouslySetInnerHTML={{
                __html: analysisResult.explanation,
              }}
            />
          </details>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="common-header">
        <h1 className="main-title">{t("urlsa.page.title")}</h1>
        <div className="subtitle-with-help">
          <p className="common-subtitle">{t("urlsa.page.subtitle")}</p>
          <HelpGuideButton guideId={4} setHeroContent={setHeroContent} />
        </div>
      </div>

      <div className="analysis-layout two-panel">
        {/* Left Panel: Input Form */}
        <div className="left-panel">
          <div className="common-section-header">
            <h3>
              <FontAwesomeIcon icon={faLink} />
              {t("urlsa.section.enterUrl")}
            </h3>
          </div>
          <div className="panel-content scrollable-panel">
            {renderInputForm()}
          </div>
        </div>

        {/* Right Panel: Analysis Results */}
        <div className="right-panel">
          <div className="common-section-header">
            <h3>
              <FontAwesomeIcon icon={faChartBar} />
              {t("urlsa.section.results")}
            </h3>
          </div>
          <div className="panel-content">
            {analysisResult ? (
              renderResultsContent()
            ) : (
              <EmptyStatePanel
                icon={faChartBar}
                title={t("urlsa.empty.title")}
                description={t("urlsa.empty.description")}
                className="empty-state-content"
              />
            )}
          </div>
        </div>
      </div>

      <DisclosuresModal disclaimerText={t("urlsa.disclaimer")} />
    </>
  );
};

export default URLSafetyAnalysisDesktop;
